import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import InviteMemberModal from '../components/InviteMemberModal';
import { apiService } from '../services/api';
import { Project, ProjectMember, ProjectEffectiveMember, ProjectGroup, Group, ProjectRole, AccessSource } from '../types/api';
import { useProjectCost } from '../hooks/useProjectCost';
import { useOrganisation } from '../contexts/OrganisationContext';
import { useAuth } from '../contexts/AuthContext';
import { SuccessMessages, ErrorMessages, getApiErrorMessage } from '../constants/messages';
import Logger from '../utils/logger';
import { useTranslation } from 'react-i18next';

const ProjectDetailPage: React.FC = () => {
  const { t } = (useTranslation as any)('projects');
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  // Acces effectifs (directs + groupes + organisation) et groupes accordes au projet
  const [access, setAccess] = useState<ProjectEffectiveMember[]>([]);
  const [projectGroups, setProjectGroups] = useState<ProjectGroup[]>([]);
  const [orgGroups, setOrgGroups] = useState<Group[]>([]);
  const [groupToGrant, setGroupToGrant] = useState('');
  const [groupRole, setGroupRole] = useState<ProjectRole>('developer');
  const [groupBusy, setGroupBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const { currentOrganisation, canManageMembers } = useOrganisation();
  const { user } = useAuth();
  const [editForm, setEditForm] = useState({
    nom: '',
    description: '',
    quota_cpu: 0,
    quota_ram: 0,
    quota_stockage: 0
  });

  // Use project cost hook
  const { estimation: projectCost, loading: costLoading, error: costError } = useProjectCost({
    cpu: project?.quota_cpu || 0,
    ram: project?.quota_ram || 0,
    storage: project?.quota_stockage || 0
  });

  useEffect(() => {
    if (projectId) {
      fetchProjectData();
    }
  }, [projectId]);

  const fetchProjectData = async () => {
    try {
      setLoading(true);
      const [projectData, membersData, accessData, groupsData] = await Promise.all([
        apiService.getProject(projectId!),
        apiService.getProjectMembers(projectId!),
        apiService.getProjectAccess(projectId!).catch(() => []),
        apiService.getProjectGroups(projectId!).catch(() => []),
      ]);
      setProject(projectData);
      setMembers(membersData);
      setAccess(accessData);
      setProjectGroups(groupsData);
      if (projectData.organisation_id) {
        setOrgGroups(await apiService.getOrganisationGroups(projectData.organisation_id).catch(() => []));
      }
      setEditForm({
        nom: projectData.nom,
        description: projectData.description || '',
        quota_cpu: projectData.quota_cpu,
        quota_ram: projectData.quota_ram,
        quota_stockage: projectData.quota_stockage
      });
    } catch (error) {
      Logger.error('Erreur lors du chargement du projet:', error);
      toast.error(ErrorMessages.PROJECT_LOAD_FAILED);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMemberRole = async (memberId: string, newRole: 'admin' | 'developer' | 'user') => {
    try {
      await apiService.updateProjectMember(projectId!, memberId, { role_projet: newRole });
      toast.success(SuccessMessages.ORG_MEMBER_ADDED);
      fetchProjectData();
    } catch (error: unknown) {
      Logger.error('Erreur lors de la mise à jour du rôle:', error);
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (window.confirm(t('detail.confirmRemoveMember'))) {
      try {
        await apiService.removeProjectMember(projectId!, memberId);
        toast.success(SuccessMessages.ORG_MEMBER_REMOVED);
        fetchProjectData();
      } catch (error: unknown) {
        Logger.error('Erreur lors de la suppression du membre:', error);
        toast.error(getApiErrorMessage(error));
      }
    }
  };

  const handleUpdateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const updatedProject = await apiService.updateProject(projectId!, editForm);
      setProject(updatedProject);
      toast.success(SuccessMessages.PROJECT_UPDATED);
      setShowEditModal(false);
    } catch (error: unknown) {
      Logger.error('Erreur lors de la mise à jour du projet:', error);
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleDeleteProject = async () => {
    if (window.confirm(t('detail.confirmDeleteProject'))) {
      try {
        await apiService.deleteProject(projectId!);
        toast.success(SuccessMessages.PROJECT_DELETED);
        navigate('/projects');
      } catch (error: unknown) {
        Logger.error('Erreur lors de la suppression du projet:', error);
        toast.error(getApiErrorMessage(error));
      }
    }
  };

  // ADMIN effectif sur le projet (direct, groupe ou organisation) : peut accorder un groupe
  const myAccess = access.find(a => a.utilisateur_id === user?.id);
  const canManageGroups = canManageMembers() || myAccess?.role_effective === 'admin';
  const inheritedAccess = access.filter(a => a.role_direct === null);
  const grantedGroupIds = new Set(projectGroups.map(g => g.groupe_id));
  const groupCandidates = orgGroups.filter(g => !grantedGroupIds.has(g.id));

  const sourceLabel = (s: AccessSource) => {
    switch (s.type) {
      case 'groupe':
        return t('detail.viaGroup', { name: s.groupe_nom });
      case 'owner_organisation':
        return t('detail.viaOrgOwner');
      case 'admin_organisation':
        return t('detail.viaOrgAdmin');
      default:
        return t('detail.viaDirect');
    }
  };

  const handleGrantGroup = async () => {
    if (!groupToGrant) return;
    setGroupBusy(true);
    try {
      await apiService.grantGroupProject(groupToGrant, projectId!, groupRole);
      toast.success(t('detail.groupGranted'));
      setGroupToGrant('');
      fetchProjectData();
    } catch (error: unknown) {
      Logger.error('Erreur lors de l attribution du groupe:', error);
      toast.error(getApiErrorMessage(error));
    } finally {
      setGroupBusy(false);
    }
  };

  const handleRevokeGroup = async (groupId: string) => {
    setGroupBusy(true);
    try {
      await apiService.revokeGroupProject(groupId, projectId!);
      toast.success(t('detail.groupRevoked'));
      fetchProjectData();
    } catch (error: unknown) {
      Logger.error('Erreur lors du retrait du groupe:', error);
      toast.error(getApiErrorMessage(error));
    } finally {
      setGroupBusy(false);
    }
  };

  const getRoleBadge = (role: string) => {
    const baseClasses = "px-2 inline-flex text-xs leading-5 font-semibold rounded-full";
    switch (role) {
      case 'admin':
        return `${baseClasses} bg-red-500/12 text-red-400`;
      case 'developer':
        return `${baseClasses} bg-blue-500/12 text-blue-400`;
      case 'user':
        return `${baseClasses} bg-green-500/12 text-green-400`;
      default:
        return `${baseClasses} bg-surface-3 text-text-primary`;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center">
        <h1 className="text-2xl font-bold text-text-primary">{t('detail.notFound')}</h1>
        <button
          onClick={() => navigate('/projects')}
          className="mt-4 bg-accent-500 hover:bg-accent-700 text-white font-bold py-2 px-4 rounded"
        >
          {t('detail.backToProjects')}
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Header du projet */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">{project.nom}</h1>
          <p className="text-text-secondary mt-2">{project.description}</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/projects')}
            className="bg-surface-3 hover:bg-surface-4 text-text-primary border border-border-1 font-bold py-2 px-4 rounded"
          >
            {`← ${t('detail.back')}`}
          </button>
          <button
            onClick={() => setShowEditModal(true)}
            className="bg-accent-500 hover:bg-accent-700 text-white font-bold py-2 px-4 rounded"
          >
            {t('detail.edit')}
          </button>
          <button
            onClick={handleDeleteProject}
            className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
          >
            {t('detail.delete')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Informations du projet */}
        <div className="bg-surface-2 overflow-hidden shadow-card rounded-card">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-text-primary mb-4">
              {t('detail.projectInfo')}
            </h3>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-text-tertiary">ID</dt>
                <dd className="mt-1 text-sm text-text-primary">{project.id}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-text-tertiary">{t('name', { ns: 'common' })}</dt>
                <dd className="mt-1 text-sm text-text-primary">{project.nom}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-text-tertiary">Description</dt>
                <dd className="mt-1 text-sm text-text-primary">{project.description || t('detail.noDescription')}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-text-tertiary">CPU</dt>
                <dd className="mt-1 text-sm text-text-primary">{project.quota_cpu} cores</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-text-tertiary">RAM</dt>
                <dd className="mt-1 text-sm text-text-primary">{project.quota_ram} GB</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-text-tertiary">{t('detail.storage')}</dt>
                <dd className="mt-1 text-sm text-text-primary">{project.quota_stockage} GB</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-text-tertiary">{t('detail.estimatedCost')}</dt>
                <dd className="mt-1 text-sm">
                  {costLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-accent-500 mr-2"></div>
                      <span className="text-text-tertiary">{t('detail.calculating')}</span>
                    </div>
                  ) : costError ? (
                    <span className="text-red-400">{t('detail.calculationError')}</span>
                  ) : projectCost ? (
                    <div className="flex items-center">
                      <span className="text-green-400 font-semibold">
                        {Math.round(projectCost.estimation.total * 655)} {t('detail.perMonth')}
                      </span>
                      <span className="text-text-tertiary text-xs ml-2">
                        ({Math.round(projectCost.estimation.total * 12 * 655)} {t('detail.perYear')})
                      </span>
                    </div>
                  ) : (
                    <span className="text-text-tertiary">{t('detail.notCalculated')}</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-text-tertiary">{t('detail.createdAt')}</dt>
                <dd className="mt-1 text-sm text-text-primary">
                  {new Date(project.date_creation).toLocaleString()}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Membres du projet */}
        <div className="bg-surface-2 overflow-hidden shadow-card rounded-card">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg leading-6 font-medium text-text-primary">
                {t('detail.members')} ({members.length})
              </h3>
              <button
                onClick={() => setShowInviteModal(true)}
                className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded text-sm"
              >
                {t('detail.invite')}
              </button>
            </div>
            <div className="space-y-3">
              {members.map((member) => (
                <div key={member.id} className="flex items-center justify-between p-3 border border-border-1 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-accent-500 rounded-full flex items-center justify-center text-white font-bold">
                      {member.prenom.substring(0, 1).toUpperCase()}{member.nom.substring(0, 1).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-text-primary">{member.prenom} {member.nom}</p>
                      <p className="text-xs text-text-tertiary">{member.email}</p>
                      <span className={getRoleBadge(member.role_projet)}>
                        {member.role_projet}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <select
                      value={member.role_projet}
                      onChange={(e) => handleUpdateMemberRole(member.id, e.target.value as 'admin' | 'developer' | 'user')}
                      className="text-sm bg-surface-3 border border-border-1 text-text-primary rounded-input focus:ring-accent-500 focus:border-accent-500"
                    >
                      <option value="user">User</option>
                      <option value="developer">Developer</option>
                      <option value="admin">Admin</option>
                    </select>
                    <button
                      onClick={() => handleRemoveMember(member.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Acces herites (groupes, organisation) et groupes accordes */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-surface-2 overflow-hidden shadow-card rounded-card">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-text-primary mb-1">
              {t('detail.inheritedAccess')} ({inheritedAccess.length})
            </h3>
            <p className="text-xs text-text-tertiary mb-4">{t('detail.inheritedAccessHint')}</p>
            <div className="space-y-3">
              {inheritedAccess.map((a) => (
                <div key={a.utilisateur_id} className="flex items-center justify-between p-3 border border-border-1 rounded-lg">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="w-8 h-8 bg-surface-3 rounded-full flex items-center justify-center text-text-secondary font-bold flex-shrink-0">
                      {a.prenom.substring(0, 1).toUpperCase()}{a.nom.substring(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-text-primary truncate">{a.prenom} {a.nom}</p>
                      <p className="text-xs text-text-tertiary truncate">{a.email}</p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {a.sources.map((s, i) => (
                          <span key={i} className="px-2 inline-flex text-xs leading-5 rounded-full bg-surface-3 text-text-secondary">
                            {sourceLabel(s)}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <span className={getRoleBadge(a.role_effective)}>{a.role_effective}</span>
                </div>
              ))}
              {inheritedAccess.length === 0 && (
                <p className="text-sm text-text-tertiary">{t('detail.noInheritedAccess')}</p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-surface-2 overflow-hidden shadow-card rounded-card">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-text-primary mb-1">
              {t('detail.groups')} ({projectGroups.length})
            </h3>
            <p className="text-xs text-text-tertiary mb-4">{t('detail.groupsHint')}</p>

            {canManageGroups && project.organisation_id && (
              <div className="flex space-x-2 mb-4">
                <select
                  value={groupToGrant}
                  onChange={(e) => setGroupToGrant(e.target.value)}
                  className="flex-1 text-sm bg-surface-3 border border-border-1 text-text-primary rounded-input py-2 px-3 focus:ring-accent-500 focus:border-accent-500"
                >
                  <option value="">{groupCandidates.length ? t('detail.selectGroup') : t('detail.noGroupCandidates')}</option>
                  {groupCandidates.map(g => <option key={g.id} value={g.id}>{g.nom}</option>)}
                </select>
                <select
                  value={groupRole}
                  onChange={(e) => setGroupRole(e.target.value as ProjectRole)}
                  className="text-sm bg-surface-3 border border-border-1 text-text-primary rounded-input py-2 px-3 focus:ring-accent-500 focus:border-accent-500"
                >
                  <option value="user">User</option>
                  <option value="developer">Developer</option>
                  <option value="admin">Admin</option>
                </select>
                <button
                  onClick={handleGrantGroup}
                  disabled={!groupToGrant || groupBusy}
                  className="bg-accent-600 hover:bg-accent-700 text-white font-medium py-2 px-4 rounded text-sm disabled:opacity-50"
                >
                  {t('detail.grantGroup')}
                </button>
              </div>
            )}

            <div className="space-y-3">
              {projectGroups.map((g) => (
                <div key={g.groupe_id} className="flex items-center justify-between p-3 border border-border-1 rounded-lg">
                  <div className="min-w-0">
                    <button
                      onClick={() => navigate(`/organisations/${project.organisation_id}/groups/${g.groupe_id}`)}
                      className="text-sm font-medium text-text-primary hover:text-accent-400 text-left truncate"
                    >
                      {g.nom}
                    </button>
                    <p className="text-xs text-text-tertiary">
                      {t('detail.groupMembers', { count: g.member_count })}
                      {g.source === 'entra' && ` · ${t('detail.groupSynced')}`}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={getRoleBadge(g.role_projet)}>{g.role_projet}</span>
                    {canManageGroups && (
                      <button
                        onClick={() => handleRevokeGroup(g.groupe_id)}
                        disabled={groupBusy}
                        className="text-red-400 hover:text-red-300 disabled:opacity-50"
                        title={t('detail.revokeGroup') as string}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {projectGroups.length === 0 && (
                <p className="text-sm text-text-tertiary">{t('detail.noGroups')}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Actions rapides */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button
          onClick={() => navigate(`/environments/${projectId}`)}
          className="bg-accent-600 hover:bg-accent-700 text-white p-4 rounded-lg text-center"
        >
          <h4 className="font-bold">{t('detail.environments')}</h4>
          <p className="text-sm">{t('detail.manageEnvironments')}</p>
        </button>
        <button
          onClick={() => navigate(`/project/${projectId}/applications`)}
          className="bg-green-600 hover:bg-green-700 text-white p-4 rounded-lg text-center"
        >
          <h4 className="font-bold">{t('detail.applications')}</h4>
          <p className="text-sm">{t('detail.viewDeployedApps')}</p>
        </button>
      </div>

      {/* Modal d'invitation avec sélection membre org ou email externe */}
      <InviteMemberModal
        projectId={projectId!}
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={fetchProjectData}
      />

      {/* Modal d'édition */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-border-1 w-96 shadow-lg rounded-md bg-surface-2">
            <h3 className="text-lg font-bold text-text-primary mb-4">{t('detail.editProject')}</h3>
            <form onSubmit={handleUpdateProject}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-text-secondary">{t('name', { ns: 'common' })}</label>
                <input
                  type="text"
                  required
                  value={editForm.nom}
                  onChange={(e) => setEditForm({ ...editForm, nom: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-text-secondary">Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                  rows={3}
                />
              </div>
              {currentOrganisation && currentOrganisation.quota_global_cpu !== -1 && (
                <p className="text-xs text-text-tertiary mb-2">
                  {t('detail.planLimits', { plan: currentOrganisation.plan_type, cpu: currentOrganisation.quota_global_cpu, ram: currentOrganisation.quota_global_ram, storage: currentOrganisation.quota_global_stockage })}
                </p>
              )}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary">CPU (vCPU)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max={currentOrganisation && currentOrganisation.quota_global_cpu !== -1 ? currentOrganisation.quota_global_cpu : undefined}
                    value={editForm.quota_cpu}
                    onChange={(e) => setEditForm({ ...editForm, quota_cpu: parseFloat(e.target.value) })}
                    className="mt-1 block w-full px-3 py-2 bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary">RAM (GB)</label>
                  <input
                    type="number"
                    min="0"
                    max={currentOrganisation && currentOrganisation.quota_global_ram !== -1 ? currentOrganisation.quota_global_ram : undefined}
                    value={editForm.quota_ram}
                    onChange={(e) => setEditForm({ ...editForm, quota_ram: parseInt(e.target.value) })}
                    className="mt-1 block w-full px-3 py-2 bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary">{t('detail.storage')} (GB)</label>
                  <input
                    type="number"
                    min="0"
                    max={currentOrganisation && currentOrganisation.quota_global_stockage !== -1 ? currentOrganisation.quota_global_stockage : undefined}
                    value={editForm.quota_stockage}
                    onChange={(e) => setEditForm({ ...editForm, quota_stockage: parseInt(e.target.value) })}
                    className="mt-1 block w-full px-3 py-2 bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm font-medium text-text-secondary bg-surface-3 rounded-md hover:bg-surface-4"
                >
                  {t('cancel', { ns: 'common' })}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-accent-600 rounded-md hover:bg-accent-700"
                >
                  {t('detail.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectDetailPage;
